import { useState, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import type { EnrollPatientResponse, TrialListQuery } from '@ctms/contracts';
import { z } from 'zod';
import { AlertCircle, UserPlus, X } from 'lucide-react';

import { getApiErrorMessage } from '../../../../api/client.js';
import { trialsApi } from '../../../trials/trials.api.js';
import { trialQueryKeys } from '../../../trials/trials.query-keys.js';
import { useTrialRandomization } from '../../../trials/use-trial.js';
import { useEnrollPatientMutation } from '../../use-enroll-patient.js';

const ACTIVE_TRIALS_QUERY = { page: 1, limit: 100, active: true } as const satisfies TrialListQuery;

const EnrollPatientFormSchema = z.object({
  trialId: z
    .string()
    .min(1, 'Wybór badania jest wymagany.')
    .refine((val) => !Number.isNaN(Number(val)) && Number(val) > 0, {
      message: 'Wybór badania jest wymagany.',
    }),
  screeningNumber: z
    .string()
    .trim()
    .min(1, 'Numer screeningowy jest wymagany.')
    .max(64, 'Maksymalnie 64 znaki.'),
  randomizationNumber: z.string().trim().max(64, 'Maksymalnie 64 znaki.').optional(),
  travelCosts: z.boolean(),
  pregnancyTest: z.boolean(),
});

type EnrollPatientFormInput = z.infer<typeof EnrollPatientFormSchema>;

export interface EnrollPatientDialogProps {
  /** The patient card being enrolled (`patients.id`). */
  patientId: number;
  /** Presentation name of the patient (e.g. `Jan Kowalski`). */
  patientName: string;
  /** Active trial from cockpit header, or `null` if none picked. */
  activeTrialId: number | null;
  /** Called after successful server response. */
  onSaved: (response: EnrollPatientResponse) => void;
  onClose: () => void;
}

const fieldClass =
  'mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-xs text-slate-100 scheme-dark';
const labelClass = 'text-xs text-slate-400';

/**
 * REQ-PAT-009…014: Enrolls a patient into a clinical trial.
 * Generates qualification-stage plan visits (REQ-PAT-025).
 * Opened from the patient chart bar or panel.
 */
export function EnrollPatientDialog({
  patientId,
  patientName,
  activeTrialId,
  onSaved,
  onClose,
}: EnrollPatientDialogProps): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<EnrollPatientFormInput>({
    resolver: zodResolver(EnrollPatientFormSchema),
    defaultValues: {
      trialId: activeTrialId !== null ? String(activeTrialId) : '',
      screeningNumber: '',
      randomizationNumber: '',
      travelCosts: false,
      pregnancyTest: false,
    },
  });

  const trialsQuery = useQuery({
    queryKey: trialQueryKeys.list(ACTIVE_TRIALS_QUERY),
    queryFn: () => trialsApi.listTrials(ACTIVE_TRIALS_QUERY),
    staleTime: 5 * 60 * 1000,
  });

  const trials = trialsQuery.data?.items ?? [];
  const activeTrial = activeTrialId !== null ? trials.find((t) => t.id === activeTrialId) : null;

  // B.1/B.2: the target trial decides whether the second number is offered —
  // the active trial when one is picked, otherwise the one chosen in the form.
  const formTrialId = watch('trialId');
  const targetTrialId = formTrialId.length > 0 ? Number(formTrialId) : null;
  const randomizationEnabled = useTrialRandomization(targetTrialId);

  const enrollMutation = useEnrollPatientMutation();
  const isSubmitting = enrollMutation.isPending;

  const onSubmit = (data: EnrollPatientFormInput): void => {
    setErrorMessage(null);
    enrollMutation.mutate(
      {
        patientId,
        request: {
          trialId: Number(data.trialId),
          screeningNumber: data.screeningNumber.trim(),
          randomizationNumber:
            data.randomizationNumber && data.randomizationNumber.trim().length > 0
              ? data.randomizationNumber.trim()
              : null,
          travelCosts: data.travelCosts,
          pregnancyTest: data.pregnancyTest,
        },
      },
      {
        onSuccess: (response) => {
          onSaved(response);
        },
        onError: async (err: unknown) => {
          setErrorMessage(
            await getApiErrorMessage(err, 'Nie udało się włączyć pacjenta do badania.'),
          );
        },
      },
    );
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="enroll-patient-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
    >
      <div className="w-full max-w-md rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-800 pb-4">
          <div>
            <h2
              id="enroll-patient-title"
              className="flex items-center gap-2 text-base font-semibold text-slate-100"
            >
              <UserPlus className="h-4 w-4 text-cyan-400" />
              Włącz do badania
            </h2>
            <p className="mt-1 text-xs text-slate-400">
              Pacjent: <span className="font-medium text-slate-200">{patientName}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Zamknij"
            className="rounded-md p-1.5 text-slate-400 hover:text-slate-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form
          onSubmit={(event) => {
            void handleSubmit(onSubmit)(event);
          }}
          className="mt-5 space-y-4"
          noValidate
        >
          {errorMessage !== null && (
            <div
              role="alert"
              className="flex items-center gap-2 rounded-md border border-rose-800/60 bg-rose-950/40 p-3 text-xs text-rose-300"
            >
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          {activeTrialId !== null ? (
            <div>
              <span className={labelClass}>Badanie</span>
              <p className="mt-1 text-xs font-semibold text-cyan-300">
                {activeTrial ? `${activeTrial.shortcut} — ${activeTrial.shortTitle}` : `Badanie #${activeTrialId}`}
              </p>
              {/* Keep value registered in the form */}
              <input type="hidden" value={String(activeTrialId)} {...register('trialId')} />
            </div>
          ) : (
            <div>
              <label htmlFor="enroll-trial-select" className={labelClass}>
                Badanie *
              </label>
              <select
                id="enroll-trial-select"
                disabled={isSubmitting}
                className={fieldClass}
                {...register('trialId')}
              >
                <option value="">— wybierz badanie —</option>
                {trials.map((trial) => (
                  <option key={trial.id} value={String(trial.id)}>
                    {trial.shortcut} — {trial.shortTitle}
                  </option>
                ))}
              </select>
              {errors.trialId !== undefined && (
                <p className="mt-1 text-xs text-rose-400">{errors.trialId.message}</p>
              )}
            </div>
          )}

          <div>
            <label htmlFor="enroll-screening-number" className={labelClass}>
              Numer screeningowy *
            </label>
            <input
              id="enroll-screening-number"
              type="text"
              placeholder="np. SCR-001"
              disabled={isSubmitting}
              className={fieldClass}
              {...register('screeningNumber')}
            />
            {errors.screeningNumber !== undefined && (
              <p className="mt-1 text-xs text-rose-400">{errors.screeningNumber.message}</p>
            )}
          </div>

          {/* B.1/B.2: no randomization field when the target trial keeps no number. */}
          {randomizationEnabled && (
            <div>
              <label htmlFor="enroll-randomization-number" className={labelClass}>
                Numer randomizacyjny (opcjonalny)
              </label>
              <input
                id="enroll-randomization-number"
                type="text"
                placeholder="np. RND-001"
                disabled={isSubmitting}
                className={fieldClass}
                {...register('randomizationNumber')}
              />
              {errors.randomizationNumber !== undefined && (
                <p className="mt-1 text-xs text-rose-400">{errors.randomizationNumber.message}</p>
              )}
            </div>
          )}

          <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-3 space-y-3">
            <div className="flex items-start gap-2.5">
              <input
                id="enroll-travel-costs"
                type="checkbox"
                disabled={isSubmitting}
                className="mt-0.5 h-3.5 w-3.5 rounded border-slate-700 bg-slate-900 text-cyan-600 focus:ring-cyan-500/20"
                {...register('travelCosts')}
              />
              <div>
                <label
                  htmlFor="enroll-travel-costs"
                  className="cursor-pointer text-xs font-medium text-slate-200"
                >
                  Koszty dojazdu
                </label>
                <p className="text-[11px] text-slate-400">
                  Zwrot kosztów dojazdu pacjenta na wizyty
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2.5">
              <input
                id="enroll-pregnancy-test"
                type="checkbox"
                disabled={isSubmitting}
                className="mt-0.5 h-3.5 w-3.5 rounded border-slate-700 bg-slate-900 text-cyan-600 focus:ring-cyan-500/20"
                {...register('pregnancyTest')}
              />
              <div>
                <label
                  htmlFor="enroll-pregnancy-test"
                  className="cursor-pointer text-xs font-medium text-slate-200"
                >
                  Test ciążowy
                </label>
                <p className="text-[11px] text-slate-400">
                  Wymagane wykonywanie testów ciążowych w ramach protokołu
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t border-slate-800 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500 disabled:opacity-50"
            >
              {isSubmitting ? 'Zapisywanie…' : 'Włącz do badania'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
